import { useMemo } from "react";
import { Landmark } from "lucide-react";
import PlaidConnectButton from "./PlaidConnectButton";
import { fmtMoney, cardCls, PAY_METHODS } from "./accountingUtils";

const BAR_COLORS = ["#1a3fd1", "#06b6d4", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6"];

const Head = ({ caption, title }) => (
  <>
    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{caption}</div>
    <div className="mt-0.5 text-sm font-semibold text-white">{title}</div>
  </>
);

/**
 * AccountingHome — the dashboard ("Today") cards, stacked single-column on
 * phones, two columns from md up, in the same order as the reference:
 * Bank accounts · Unpaid invoices · Paid | Mileage · Expenses · P&L · Cash.
 */
const AccountingHome = ({ base, readOnly, items, newTx, monthExpenses, summary, onOpenExpenses, onOpenBanking, onOpenInvoices, onOpenMileage }) => {
  const total = summary?.expenses_month ?? monthExpenses.reduce((s, e) => s + (Number(e.total) || 0), 0);
  const income = summary?.income_month || 0;
  const mileage = summary?.mileage_month?.amount || 0;
  const net = summary ? summary.net_month : -total;
  const maxBar = Math.max(income, total + mileage, 1);
  const unpaid = summary?.unpaid;
  const paid30 = summary?.paid_30;
  const tax = monthExpenses.reduce((s, e) => s + (Number(e.tax) || 0), 0);
  const accounts = items.flatMap((it) => (it.accounts || []).map((a) => ({ ...a, bank: it.institution_name })));
  const bankTotal = accounts.reduce((s, a) => s + (Number(a.balance_current) || 0), 0);

  const byCategory = useMemo(() => {
    const m = new Map();
    monthExpenses.forEach((e) => {
      const lines = e.lines?.length ? e.lines : [{ category: e.category, amount: e.subtotal }];
      lines.forEach((l) => m.set(l.category || "Uncategorized", (m.get(l.category || "Uncategorized") || 0) + (Number(l.amount) || 0)));
    });
    const sub = [...m.values()].reduce((s, v) => s + v, 0) || 1;
    return [...m.entries()].map(([k, v]) => ({ k, v, pct: (v / sub) * 100 })).sort((a, b) => b.v - a.v).slice(0, 6);
  }, [monthExpenses]);

  const byMethod = useMemo(() => {
    const m = {};
    monthExpenses.forEach((e) => { m[e.payment_method] = (m[e.payment_method] || 0) + (Number(e.total) || 0); });
    return PAY_METHODS.filter((p) => m[p.key]).map((p) => ({ ...p, v: m[p.key] }));
  }, [monthExpenses]);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="acct-home">
      {/* Bank accounts */}
      <div className={cardCls} data-testid="acct-card-bank">
        <Head caption="Bank accounts" title={accounts.length ? fmtMoney(bankTotal) : "Not connected"} />
        <div className="mt-1 text-xs text-white/50">{accounts.length ? "Total bank balance" : "Connect a bank to pull transactions in automatically."}</div>
        {accounts.length > 0 && (
          <ul className="mt-3 divide-y divide-white/10">
            {accounts.map((a) => (
              <li key={a.account_id} className="flex items-center justify-between py-2 text-sm">
                <span className="min-w-0 truncate text-white/80"><Landmark className="mr-2 inline h-3.5 w-3.5 text-white/40" />{a.bank} · {a.name || a.subtype} <span className="font-mono text-white/40">••{a.mask}</span></span>
                <span className="ml-2 shrink-0 font-mono text-white">{a.balance_current != null ? fmtMoney(a.balance_current) : "—"}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!readOnly && !accounts.length && <PlaidConnectButton base={base} onConnected={onOpenBanking} />}
          <button type="button" onClick={onOpenBanking} data-testid="acct-card-bank-review" className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/80 hover:border-white/25">
            {newTx > 0 ? `${newTx} transaction${newTx === 1 ? "" : "s"} to review` : "Banking"}
          </button>
        </div>
      </div>

      <button type="button" onClick={onOpenInvoices} className={`${cardCls} text-left transition-colors hover:border-white/25`} data-testid="acct-card-unpaid">
        <Head caption="Last 365 days" title="Unpaid invoices" />
        <div className="mt-2 font-mono text-3xl font-bold text-white">{fmtMoney(unpaid?.total || 0)}</div>
        {unpaid?.count ? (
          <>
            <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (unpaid.overdue_total / (unpaid.total || 1)) * 100)}%` }} /></div>
            <div className="mt-2 flex justify-between text-xs"><span className="text-amber-300">{fmtMoney(unpaid.overdue_total)} · {unpaid.overdue_count} overdue</span><span className="text-white/60">{fmtMoney(unpaid.total - unpaid.overdue_total)} · {unpaid.count - unpaid.overdue_count} not due yet</span></div>
            <ul className="mt-3 divide-y divide-white/10">
              {unpaid.top.map((i) => <li key={i.id} className="flex items-center justify-between py-2 text-sm"><span className="min-w-0 truncate text-white/80">{i.customer_name || `Invoice #${i.number}`}<span className="block text-[10px] text-white/40">{i.status === "overdue" ? "overdue" : "due"} {i.due_date}</span></span><span className="font-mono text-white">{fmtMoney(i.balance)}</span></li>)}
            </ul>
          </>
        ) : <div className="mt-1 text-xs text-white/50">No open invoices.</div>}
      </button>

      <div className="grid grid-cols-2 gap-3 md:col-span-2">
        <button type="button" onClick={onOpenInvoices} className={`${cardCls} text-left transition-colors hover:border-white/25`} data-testid="acct-card-paid">
          <Head caption="Last 30 days" title="Paid invoices" />
          <div className="mt-2 font-mono text-2xl font-bold text-white">{fmtMoney(paid30?.total || 0)}</div>
          <div className="mt-1 text-xs text-white/50">{paid30?.count || 0} paid</div>
        </button>
        <button type="button" onClick={onOpenMileage} className={`${cardCls} text-left transition-colors hover:border-white/25`} data-testid="acct-card-mileage">
          <Head caption="This month" title="Mileage" />
          <div className="mt-2 font-mono text-2xl font-bold text-white">{fmtMoney(mileage)}</div>
          <div className="mt-1 text-xs text-white/50">{summary?.mileage_month?.km || 0} km · {summary?.mileage_month?.trips || 0} trips</div>
        </button>
      </div>

      {/* Expenses this month */}
      <button type="button" onClick={onOpenExpenses} className={`${cardCls} text-left transition-colors hover:border-white/25`} data-testid="acct-card-expenses">
        <Head caption="This month" title="Expenses" />
        <div className="mt-2 font-mono text-3xl font-bold text-white" data-testid="acct-card-expenses-total">{fmtMoney(total)}</div>
        <div className="mt-1 text-xs text-white/50">{monthExpenses.length} entr{monthExpenses.length === 1 ? "y" : "ies"} · {fmtMoney(tax)} tax{newTx > 0 ? ` · ${newTx} to review` : ""}</div>
        {byCategory.length > 0 && (
          <>
            <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-white/10">
              {byCategory.map((c, i) => <div key={c.k} style={{ width: `${c.pct}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />)}
            </div>
            <ul className="mt-3 space-y-1.5">
              {byCategory.map((c, i) => (
                <li key={c.k} className="flex items-center justify-between gap-2 text-xs">
                  <span className="flex min-w-0 items-center gap-2 text-white/70">
                    <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }} />
                    <span className="truncate">{c.k}</span>
                    <span className="text-white/40">· {Math.round(c.pct)}%</span>
                  </span>
                  <span className="font-mono text-white">{fmtMoney(c.v)}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </button>

      {/* Profit & loss */}
      <div className={cardCls} data-testid="acct-card-pnl">
        <Head caption="This month" title="Profit and Loss" />
        <div className={`mt-2 font-mono text-3xl font-bold ${net < 0 ? "text-red-300" : "text-white"}`} data-testid="acct-card-pnl-net">{net < 0 ? "-" : ""}{fmtMoney(Math.abs(net))}</div>
        <div className="mt-1 text-xs text-white/50">Net income</div>
        <div className="mt-4 space-y-3">
          <div>
            <div className="flex justify-between text-xs text-white/60"><span>Income</span><span className="font-mono">{fmtMoney(income)}</span></div>
            <div className="mt-1 h-2 w-full rounded-full bg-white/10"><div className="h-2 rounded-full bg-emerald-500" style={{ width: `${(income / maxBar) * 100}%` }} /></div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-white/60"><span>Expenses{mileage > 0 ? " + mileage" : ""}</span><span className="font-mono">{fmtMoney(total + mileage)}</span></div>
            <div className="mt-1 h-2 w-full rounded-full bg-white/10"><div className="h-2 rounded-full bg-[#1a3fd1]" style={{ width: `${((total + mileage) / maxBar) * 100}%` }} /></div>
          </div>
        </div>
        {byMethod.length > 0 && (
          <ul className="mt-4 divide-y divide-white/10 border-t border-white/10">
            {byMethod.map((m) => (
              <li key={m.key} className="flex items-center justify-between py-2 text-xs"><span className="text-white/60">Paid by {m.label.toLowerCase()}</span><span className="font-mono text-white">{fmtMoney(m.v)}</span></li>
            ))}
          </ul>
        )}
      </div>

      {/* Cash balance */}
      <div className={`${cardCls} md:col-span-2`} data-testid="acct-card-cash">
        <Head caption="Today" title="Cash balance" />
        <div className="mt-2 font-mono text-3xl font-bold text-white">{accounts.length ? fmtMoney(bankTotal) : "—"}</div>
        <div className="mt-1 text-xs text-white/50">{accounts.length ? "Across all connected accounts. Trend chart comes next." : "Connect a bank to see your balance here."}</div>
      </div>
    </div>
  );
};

export default AccountingHome;
